import { isPresent } from "@apl-digital/utils";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { ProductLayouts } from "@/api/controller/productLayouts";
import type { GetProductLayoutsRequest } from "@/api/interfaces/productLayouts";
import { hasData, isLoading } from "@/store/apiRequestStatusMachine";
import { selectConfiguration } from "@/store/salesPoint/slice";
import { selectCurrentDiningOption } from "@/store/shoppingCart/selectors";
import type { AppThunk } from "@/store/store";
import type { AppAsyncThunkConfig } from "@/store/types";
import { getBrandTheme } from "@/theme";
import i18n from "@/translation";

import { applySectionColors } from "./helper";
import type { ProductLayoutData } from "./types";

export const fetchProductLayout = createAsyncThunk<
  ProductLayoutData,
  GetProductLayoutsRequest,
  AppAsyncThunkConfig
>(
  "products/fetchProductLayout",
  async (parameters, { rejectWithValue, signal }) => {
    const { config } = getBrandTheme();
    const language = i18n.language;

    const response = await ProductLayouts.getProductLayouts({
      signal,
      language,
      ...parameters,
    });

    if (response?.isResponseOk) {
      const data = response.response;

      return {
        userId: data?.userId,
        sections: applySectionColors(
          data?.sections ?? [],
          config.sectionsColors.split(";"),
        ),
        products: data?.products ?? [],
      };
    }

    return rejectWithValue(response);
  },
  {
    condition: (_, { getState }) => {
      const productLayout = getState().products.productLayout;

      if (isLoading(productLayout)) {
        if (process.env.NODE_ENV === "development") {
          console.log(
            "%cCancelled action products/fetchProductLayout because it was already loading.",
            "color:red",
          );
        }
        return false;
      }
    },
  },
);

const retryProductLayoutThunk: AppThunk = (dispatch, getState) => {
  const state = getState();
  const configuration = selectConfiguration(state);

  if (!hasData(configuration)) {
    return;
  }

  const layoutID = configuration.state.ProductSalesLayoutId;
  const diningOption = selectCurrentDiningOption(state);

  if (!isPresent(diningOption)) {
    return;
  }

  void dispatch(
    fetchProductLayout({
      productLayoutID: layoutID,
      diningOptionId: diningOption.ID,
    }),
  );
};

export const retryProductLayout = () => retryProductLayoutThunk;
